import classNames from 'classnames';
import { format, parse } from 'date-fns';

import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { toast } from 'react-toastify';

import { AuthApi, CommonApi } from '../api';
import { AuthRoute, Select } from '../components';
import { useAuthContext } from '../hooks';
import { ClientAuthLayout } from '../layouts';
import { normalizePhoneNumber } from '../utils';

interface RegistrationFormFields {
  name: string;
  midname: string;
  surname: string;
  gender_id: number;
  birthdate: string;
  username: string;
  email: string;
  password: string;
  hospital_id: number;
  code?: string;
}

interface SendEmailParams {
  email: string;
  hospitalId: string;
}

export const ClientSignupPage = () => {
  const { signIn } = useAuthContext();

  const { data: hospitals = [] } = useQuery({
    queryKey: [CommonApi.KEY, 'getHospitals'],
    queryFn: CommonApi.getHospitals,
  });

  const { data: genderList = [] } = useQuery({
    queryKey: [CommonApi.KEY, 'getGenders', 107],
    queryFn: () => CommonApi.getGenders(107),
  });

  const [isCodeSent, setIsCodeSent] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<RegistrationFormFields>();

  const { mutate: sendEmailMutation, isLoading: isSendingEmail } = useMutation({
    mutationFn: async ({ email, hospitalId }: SendEmailParams) =>
      AuthApi.sendEmail(email, hospitalId),
    onSuccess: () => {
      setIsCodeSent(true);
    },
    onError: () => {
      toast.error('Ошибка при отправке кода. Попробуйте снова.');
    },
  });

  const { mutate: registerClientMutation, isLoading: isRegistering } =
    useMutation({
      mutationFn: (data: RegistrationFormFields) =>
        AuthApi.registerClient(data),
      onSuccess: (response) => {
        const userId = response.data.userId;

        signIn(userId, true, {
          isProfileCompleted: response.data.isProfileCompleted,
          legal_agreement_accepted: response.data.legal_agreement_accepted,
          legal_agreement_version: response.data.legal_agreement_version,
        });
      },
      onError: () => {
        toast.error('Ошибка при подтверждении кода. Попробуйте снова.');
      },
    });

  const onSubmit: SubmitHandler<RegistrationFormFields> = useCallback(
    (data) => {
      const normalizedPhone = normalizePhoneNumber(data.username);
      const parsedDate = parse(data.birthdate, 'dd-MM-yyyy', new Date());
      const formattedDate = format(parsedDate, 'yyyy-MM-dd');
      if (isCodeSent) {
        registerClientMutation({
          ...data,
          username: normalizedPhone,
          birthdate: formattedDate,
        });
      } else {
        sendEmailMutation({
          email: data.email,
          hospitalId: data.hospital_id.toString(),
        });
      }
    },
    [isCodeSent, registerClientMutation, sendEmailMutation],
  );

  return (
    <AuthRoute>
      <ClientAuthLayout>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="mx-auto grid w-full max-w-[360px] gap-[16px]"
        >
          <h1 className="mb-[36px] text-center text-[30px] font-medium leading-[36px]">
            Регистрация
          </h1>

          <input
            {...register('name', { required: true })}
            className={classNames('input', errors.name && 'input--invalid')}
            placeholder="Имя"
            type="text"
          />

          <input
            {...register('surname', { required: true })}
            className={classNames('input', errors.surname && 'input--invalid')}
            placeholder="Фамилия"
            type="text"
          />

          <input
            {...register('midname', { required: true })}
            className={classNames('input', errors.midname && 'input--invalid')}
            placeholder="Отчество"
            type="text"
          />

          <Controller
            control={control}
            name="gender_id"
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <Select
                error={fieldState.error}
                onChange={field.onChange}
                options={genderList.map((gender) => ({
                  label: gender.value,
                  value: gender.id,
                }))}
                placeholder="Пол"
                value={field.value}
              />
            )}
          />

          <Controller
            control={control}
            name="username"
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <InputMask
                {...field}
                alwaysShowMask
                className={classNames(
                  'input',
                  fieldState.invalid && 'input--invalid',
                )}
                mask="+7 999 999 99 99"
                maskPlaceholder={null}
                placeholder="Номер телефона"
                type="tel"
                value={field.value ?? ''}
              />
            )}
          />

          <Controller
            control={control}
            name="birthdate"
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <InputMask
                {...field}
                alwaysShowMask
                className={classNames(
                  'input',
                  fieldState.invalid && 'input--invalid',
                )}
                mask="99-99-9999"
                maskPlaceholder={null}
                placeholder="Дата рождения (ДД-ММ-ГГГГ)"
                type="tel"
                value={field.value ?? ''}
              />
            )}
          />

          <input
            {...register('email', {
              required: true,
              pattern: /^\S+@\S+$/,
            })}
            className={classNames('input', errors.email && 'input--invalid')}
            placeholder="Email"
            type="email"
          />

          <input
            {...register('password', { required: true })}
            className={classNames('input', errors.password && 'input--invalid')}
            placeholder="Пароль"
            type="password"
          />

          <Controller
            control={control}
            name="hospital_id"
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <Select
                error={fieldState.error}
                onChange={field.onChange}
                options={hospitals.map((hospital) => ({
                  label: hospital.name,
                  value: hospital.id,
                }))}
                placeholder="Госпиталь"
                value={field.value}
              />
            )}
          />
          {isCodeSent && (
            <input
              {...register('code', { required: true, maxLength: 4 })}
              className={classNames('input', errors.code && 'input--invalid')}
              placeholder="Введите код"
              type="text"
              maxLength={4}
            />
          )}

          <button
            className="button button--block button--primary"
            type="submit"
            disabled={isSendingEmail || isRegistering}
          >
            {isSendingEmail || isRegistering
              ? 'Загрузка...'
              : isCodeSent
              ? 'Завершить регистрацию'
              : 'Отправить код'}
          </button>
        </form>
      </ClientAuthLayout>
    </AuthRoute>
  );
};
